import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { getPublicBusiness, mediaUrl, API } from "@/api";
import { BrandScope } from "@/theme";
import { Button } from "./ui/button";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const QrPoster = () => {
  const { slug } = useParams();
  const [business, setBusiness] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => { getPublicBusiness(slug).then(setBusiness).catch(() => setNotFound(true)); }, [slug]);

  if (notFound) return <div className="min-h-screen flex items-center justify-center text-slate-500">Betrieb nicht gefunden.</div>;
  if (!business) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const url = `${window.location.origin}/join/${slug}`;
  const logo = mediaUrl(business.logo_url);
  const isPoints = business.loyalty_mode === "points_spend";
  const collectLine = isPoints
    ? `${business.reward_threshold} Punkte sammeln`
    : `${business.stamps_per_card} Stempel sammeln`;
  const initial = business.name?.[0] || "B";

  const downloadPdf = async (fmt) => {
    setBusy(fmt);
    try {
      const res = await fetch(`${API}/poster/${slug}/pdf?format=${fmt}&url=${encodeURIComponent(url)}`);
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = fmt === "a5" ? `${slug}-qr-aushang-a5.pdf` : `${slug}-qr-aushang-a4-druckbogen.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch (e) {
      toast.error("PDF konnte nicht erstellt werden.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <BrandScope primary={business.primary_color} secondary={business.secondary_color}>
      <div className="min-h-screen bg-slate-50 print:bg-white">
        {/* Toolbar — hidden when printing */}
        <div className="no-print p-4 flex flex-wrap gap-3 justify-center bg-white/80 backdrop-blur border-b border-slate-200 sticky top-0 z-10">
          <Button data-testid="download-a5-button" disabled={busy !== null} onClick={() => downloadPdf("a5")} className="rounded-xl font-display font-bold press text-white" style={{ background: "var(--brand-primary)" }}>
            {busy === "a5" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} PDF herunterladen (A5)
          </Button>
          <Button data-testid="download-a4sheet-button" disabled={busy !== null} onClick={() => downloadPdf("a4sheet")} variant="outline" className="rounded-xl font-display font-bold press border-slate-300">
            {busy === "a4sheet" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} A5 auf A4-Druckbogen
          </Button>
        </div>

        {/* ---------- SCREEN VERSION (responsive) ---------- */}
        <div className="print:hidden px-4 py-8 sm:py-12 flex justify-center">
          <div data-testid="qr-poster" className="w-full max-w-xl bg-white rounded-[28px] ring-1 ring-slate-100 shadow-[0_24px_70px_-30px_rgba(15,23,42,0.30)] px-6 sm:px-10 py-9 sm:py-12 flex flex-col items-center text-center">
            {logo ? (
              <img src={logo} alt={business.name} className="w-28 h-28 sm:w-36 sm:h-36 object-contain" />
            ) : (
              <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-3xl brand-wave flex items-center justify-center"><span className="text-white font-display font-extrabold text-6xl">{initial}</span></div>
            )}
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl leading-tight text-slate-800 mt-5 tracking-tight">{business.name}</h1>
            <p className="font-display font-extrabold text-xl sm:text-2xl mt-1.5" style={{ color: "var(--brand-primary)" }}>Digitale Treuekarte</p>
            <p className="font-display font-semibold text-base sm:text-lg text-slate-500 mt-1">Jetzt QR-Code scannen</p>
            <div className="mt-7 w-full max-w-[300px] sm:max-w-[340px] p-5 sm:p-6 rounded-[28px] bg-white ring-1 ring-slate-100 shadow-[0_18px_50px_-20px_rgba(15,23,42,0.25)]">
              <QRCodeSVG data-testid="poster-qr-code" value={url} size={340} fgColor="#000000" bgColor="#FFFFFF" level="M" className="w-full h-auto" />
            </div>
            <p className="font-display font-extrabold text-xl sm:text-2xl text-slate-800 mt-8">{collectLine}</p>
            <div className="mt-3 rounded-full px-8 py-3 brand-wave shadow-brand">
              <p className="font-display font-extrabold text-lg sm:text-xl text-white">{business.reward_title}</p>
            </div>
            <p className="text-slate-400 font-semibold text-xs sm:text-sm mt-8">Keine App-Installation nötig · powered by Bonstelo</p>
          </div>
        </div>

        {/* ---------- PRINT VERSION (A4 portrait, exactly 1 page) ---------- */}
        <div data-testid="qr-poster-print" className="hidden print:flex poster-sheet bg-white flex-col items-center text-center">
          {logo ? (
            <img src={logo} alt={business.name} className="w-40 h-40 object-contain" />
          ) : (
            <div className="w-40 h-40 rounded-3xl brand-wave flex items-center justify-center"><span className="text-white font-display font-extrabold text-7xl">{initial}</span></div>
          )}
          <h1 className="font-display font-extrabold text-[3rem] leading-[1.05] text-slate-800 mt-6 tracking-tight">{business.name}</h1>
          <p className="font-display font-extrabold text-3xl mt-2" style={{ color: "var(--brand-primary)" }}>Digitale Treuekarte</p>
          <p className="font-display font-semibold text-2xl text-slate-500 mt-2">Jetzt QR-Code scannen</p>
          <div className="mt-9 p-8 rounded-[32px] bg-white ring-1 ring-slate-100 shadow-[0_22px_60px_-22px_rgba(15,23,42,0.28)]">
            <QRCodeSVG value={url} size={360} fgColor="#000000" bgColor="#FFFFFF" level="M" />
          </div>
          <p className="font-display font-extrabold text-[2.3rem] text-slate-800 mt-10">{collectLine}</p>
          <div className="mt-4 rounded-full px-10 py-4 brand-wave shadow-brand">
            <p className="font-display font-extrabold text-3xl text-white">{business.reward_title}</p>
          </div>
          <p className="text-slate-400 font-semibold text-lg mt-auto pt-10">Keine App-Installation nötig · powered by Bonstelo</p>
        </div>
      </div>
    </BrandScope>
  );
};
